import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { fetchHeadlines } from '../../../actions/news';
import useActions from '../../../utils/hooks/useActions';
import useLocalize from '../../../utils/hooks/useLocalize';
import useNewsArticleModal from '../../../utils/hooks/useNewsArticleModal';
import { newsProvider } from '../../../utils/newsProvider';
import { getResultsForCurrentPage } from '../../common/customMaterial/Pagination/PaginateCommon';
import PaginationBoxView from '../../common/customMaterial/Pagination/PaginationBoxView';
import DateTime from '../../common/DateTime';
import NewsArticleModal from '../../common/NewsArticleModal';
import './NewsTabComponent.scss';

const MAX_NEWS = 250;
const NEWS_PER_PAGE_DEFAULT = 25;


const NewsTabComponent = ({ symbol, newsPerPage = NEWS_PER_PAGE_DEFAULT }) => {
  const localize = useLocalize();
  const [getNews] = useActions([fetchHeadlines], []);

  // Custom hook for modal management
  const { open, selectedArticle, articlesRead, openArticle, closeModal } = useNewsArticleModal();

  const headlineData = useSelector(
    (state) => (state.news.headlines.data || {}).articles || []
  );
  const isLoading = useSelector((state) => state.news.isLoading || false);
  const isError = useSelector((state) => state.news.isError || false);

  const [currentPage, setCurrentPage] = useState(0);

  // Fetch news on component mount or when symbol changes
  useEffect(() => {
    if (symbol) {
      getNews(symbol, MAX_NEWS);
    }
  }, [getNews, symbol]);

  const handlePageClick = (data) => {
    setCurrentPage(data.selected);
  };

  const renderContent = () => {
    if (isLoading) {
      return <div className="loading">{localize('news.article.loading')}</div>;
    }

    if (isError) {
      return <div className="no-news">{localize('message.status.error')}</div>;
    }

    const displayedHeadlines = getResultsForCurrentPage(
      headlineData,
      currentPage + 1,
      newsPerPage
    );

    if (displayedHeadlines.length === 0) {
      return (
        <div className="no-news">{localize('symbol.details.news.no.news')}</div>
      );
    }

    return displayedHeadlines.map((article, index) => {
      const isRead = articlesRead.includes(article.articleId);
      return (
        <div
          key={article.articleId || index}
          className="news-item"
          onClick={() => openArticle(article)}
        >
          <span className="news-metadata">
            <div className="news-timestamp">
              <DateTime
                value={article.timestamp}
                hasZeroPad={true}
                hasYear={true}
                format="YYYY/MM/DD HH:mm"
              />
            </div>
            <a className={`news-title ${isRead ? 'viewed' : ''}`}>{article.title}</a>
            <span className="news-provider">
              {localize(newsProvider(article.provider))}
            </span>
          </span>
        </div>
      );
    });
  };

  return (
    <div className="news-tab-container">
      <div className="news-header">
        {localize('symbol.details.news.header.label')}
      </div>
      {renderContent()}
      <div className="pagination-container">
        <PaginationBoxView
          itemsPerPage={newsPerPage}
          totalDataSize={headlineData.length}
          onPageChange={handlePageClick}
          previousLabel={'<'}
          nextLabel={'>'}
          breakClassName={'break-me'}
          marginPagesDisplayed={1}
          containerClassName={'pagination'}
          subContainerClassName={'pages pagination'}
          activeClassName={'active'}
          forcePage={currentPage}
        />
      </div>
      <NewsArticleModal
        open={open}
        onClose={closeModal}
        article={selectedArticle}
        isWide={true}
      />
    </div>
  );
};

export default NewsTabComponent;